/* eslint-disable no-param-reassign */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useMutation } from '@apollo/client';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { v4 } from 'uuid';

import { captureException } from '@playbookux/playbook-web-core';

import TemplateQuestions from '../../../components/template-questions';
import typeTemplate from '../../../../core/constants/templateType';

import {
  CREATE_TEMPLATE,
  updateCacheOnCreateTemplate,
} from '../../../../core/graphql/templates';

import {
  setTemplateValues,
} from '../../../../core/actions';

import {
  Container,
  ModalSave,
  ModalCancel,
} from './styles';

const CreateTemplatePage = (props) => {
  const {
    history,
    companyId,
    onSetTemplateValues,
  } = props;

  const [
    createTemplate,
    { loading, error },
  ] = useMutation(CREATE_TEMPLATE);

  const [showModalCancel, setShowModalCancel] = useState(false);
  const [showModalSave, setShowModalSave] = useState(false);
  const [valuesSave, setValuesSave] = useState(null);

  const handleClose = () => {
    history.push('/template-library');
  };

  const handleShowModalCancel = () => {
    setShowModalCancel((prev) => !prev);
  };

  /**
   * @description Sanitizes valuesSave, removing imageUrl and videoUrl
   * keys from questionAttributes.
   * @returns {object}
   */
  const sanitizeValues = () => {
    if (valuesSave) {
      const sanitizedValues = valuesSave;
      const qAttrs = _.get(sanitizedValues, 'surveyAttributes.questionsAttributes');
      qAttrs.forEach((qAttr, qIndex) => {
        delete qAttr.imageUrl;
        delete qAttr.videoUrl;
        sanitizedValues.surveyAttributes.questionsAttributes[qIndex] = qAttr;
      });

      return sanitizedValues;
    }

    return null;
  };

  const handleSubmit = async () => {
    try {
      const sanitizedValues = sanitizeValues();

      await createTemplate({
        variables: {
          projectId: null,
          segmentId: null,
          attributes: {
            companyId,
            ...sanitizedValues,
          },
        },
        update: updateCacheOnCreateTemplate(typeTemplate.TEMPLATE_TEST, companyId),
      });

      handleClose();
    } catch (ex) {
      captureException('CREATE_TEMPLATE_FAILED', ex);
    }
  };

  const handleShowModalSave = (values) => {
    if (values) {
      setValuesSave(values);
    }
    setShowModalSave((prev) => !prev);
  };

  const handlePreviewStudy = (values) => {
    if (values) {
      const tasks = _.map(values.tasks, (task) => {
        const questionOptions = [];
        if (task.questionType === 'range') {
          for (let index = 1; index <= task.range; index += 1) {
            questionOptions.push({
              id: v4(), label: `${index}`, disqualify: null, qualify: false,
            });
          }
        } else if (task.questionOptions) {
          _.forEach(task.questionOptions, (question) => {
            questionOptions.push({
              id: v4(), label: question.label, disqualify: null, qualify: false,
            });
          });
        }

        return { ...task, questionOptions, id: v4() };
      });

      const previewValue = {
        ...values,
        tasks,
      };

      onSetTemplateValues(previewValue);
      window.open('/templates/preview-study');
    }
  };

  return (
    <>
      <Container>

        <TemplateQuestions
          onCancel={handleShowModalCancel}
          onSave={handleShowModalSave}
          onPreviewStudy={handlePreviewStudy}
          isEdit={false}
        />

      </Container>

      {showModalCancel && (
        <ModalCancel
          show={showModalCancel}
          error={error}
          onCancel={handleShowModalCancel}
          onConfirm={handleClose}
          isLoading={loading}
          textCancel="No, don't cancel"
          textConfirm="Yes, cancel"
          textContent="Do you want to cancel? Your updates will not be saved."
        />
      )}

      {showModalSave && (
        <ModalSave
          show={showModalSave}
          error={error}
          onCancel={handleShowModalSave}
          onConfirm={handleSubmit}
          isLoading={loading}
          textCancel="Cancel"
          textConfirm="Save"
          textContent="Do you want to save your updates?"
        />
      )}

    </>
  );
};

CreateTemplatePage.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  companyId: PropTypes.string.isRequired,
  onSetTemplateValues: PropTypes.func.isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  companyId: authentication.company?.id,
});

const mapDispatchToProps = {
  onSetTemplateValues: setTemplateValues,
};

const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(CreateTemplatePage);
